/**
 * PublicPayPage — /pay/:token (PAY-2 branded checkout). PUBLIC, no chrome.
 *
 * The page belongs to the MERCHANT: their name headlines it, the order
 * summary is ours, and Cardcom is reduced to the secure card fields inside
 * the embedded frame (PCI stays with them; the customer never leaves
 * buzzbitx.com). /pay/thanks is the in-frame return target — it renders the
 * plain "Payment received" state. Invalid/tampered tokens fail closed.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import { BrandLogo } from '@/components/brand/BrandLogo';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.buzzbitx.com';

interface PaySession {
  brandName: string;
  amount: number;
  currency: 'ILS' | 'USD';
  description: string;
  iframeUrl: string;
}

export default function PublicPayPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const isThanks = location.pathname === '/pay/thanks';

  const [session, setSession] = useState<PaySession | null>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (isThanks || !token) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/pay/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) { setInvalid(true); return; }
        const json = await res.json();
        setSession((json?.data ?? null) as PaySession | null);
      } catch {
        setInvalid(true);
      }
    })();
  }, [token, isThanks]);

  if (isThanks) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
            <Check className="h-6 w-6 text-emerald-600" />
          </span>
          <h1 className="mt-4 text-lg font-semibold text-gray-900">Payment received</h1>
          <p className="mt-1 text-sm text-gray-500">You can close this window — a receipt is on its way.</p>
        </div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-gray-300" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">This payment link is not valid</h1>
          <p className="mt-2 text-sm text-gray-500">Ask the business for a new link — this one may have been replaced.</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-400">Preparing secure checkout…</p>
      </div>
    );
  }

  const amountLabel = `${session.currency === 'USD' ? '$' : '₪'}${session.amount.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        {/* The merchant's page, not Cardcom's */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{session.brandName}</h1>
          <p className="mt-1 text-sm text-gray-500">Secure checkout</p>
        </div>

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">{session.description}</span>
            <span className="text-xl font-bold text-gray-900">{amountLabel}</span>
          </div>
        </div>

        {/* Cardcom = the card fields only, inside our page */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <iframe
            src={session.iframeUrl}
            title="Secure payment"
            className="h-[560px] w-full border-0"
            allow="payment"
          />
        </div>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-400">
          <Lock className="h-3 w-3" /> Card details are processed by a PCI-certified provider and never touch our servers.
        </p>
        <p className="mt-6 flex justify-center">
          <BrandLogo size="sm" />
        </p>
      </div>
    </div>
  );
}
